// Confirm-before-replace guard for the active Builder strategy.
//
// Until this, any "Load in Builder" / "Apply template" / "Generate" /
// "Import" / "Load best in Builder" action silently overwrote whatever was
// in the active strategy via `setStrategy(...)`. If the user had unsaved
// edits on the canvas, those edits were gone — no warning, no undo, no
// way to recover.
//
// This module provides:
//   • useReplaceStrategyGuard() — a hook each caller owns. Wraps the
//     replace call so it checks the dirty flag first; queues the pending
//     replacement when the user has unsaved work.
//   • <ReplaceStrategyConfirm /> — a dumb dialog that shows what's
//     about to be lost and asks the user to confirm.
//
// Scope guard: this only protects against destructive replacement. It is
// NOT a version history, NOT an undo system, NOT a save-then-replace
// flow. The escape hatch shown to the user is "Cancel, then save it from
// the Backtest tab" — the existing Save Run button already does that.

import React, { useCallback, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import type { Block } from '../../types';
import { useStrategy, type StrategySource } from '../../contexts/StrategyContext';

export interface PendingReplace {
  blocks: Block[];
  name: string | null;
  source: StrategySource;
  meta?: Record<string, unknown>;
  // Friendlier label used in the dialog when it would be more informative
  // than `name`. e.g. "AI optimized · BTCUSDT · 1h" vs the bare name.
  label?: string;
  // Optional callback fired AFTER the replace actually happens (either
  // immediately when not dirty, or after the user confirms). Used by
  // callers that also need to update local mirror state alongside the
  // global StrategyContext — e.g. the Builder page synchronously updating
  // its `strategyState.blocks` to avoid a one-frame flash of stale blocks.
  // Not fired on cancel.
  onComplete?: () => void;
  // 'new' tweaks the copy for "start a fresh/empty canvas" (vs loading another strategy).
  kind?: 'replace' | 'new';
  // Override the confirm button label (e.g. "Discard & start new").
  confirmLabel?: string;
}

/**
 * Hook owned by each caller that needs to replace the active strategy.
 * Wraps `strategyCtx.setStrategy` with a dirty-check + confirmation queue.
 *
 * Usage:
 *   const { pending, requestReplace, confirmReplace, cancelReplace } =
 *     useReplaceStrategyGuard();
 *
 *   // On user action to load something new:
 *   requestReplace({ blocks, name: 'Loaded variant', source: 'saved' });
 *
 *   // In JSX:
 *   <ReplaceStrategyConfirm
 *     pending={pending}
 *     onConfirm={confirmReplace}
 *     onCancel={cancelReplace}
 *   />
 *
 * If the active strategy is NOT dirty, `requestReplace` fires `setStrategy`
 * immediately and never queues — no dialog flash. The user only sees the
 * dialog when there's actual unsaved work to lose.
 */
export function useReplaceStrategyGuard() {
  const strategyCtx = useStrategy();
  const [pending, setPending] = useState<PendingReplace | null>(null);

  const requestReplace = useCallback((p: PendingReplace) => {
    if (!strategyCtx.isDirty) {
      strategyCtx.setStrategy(p.blocks, p.name, p.source, p.meta);
      p.onComplete?.();
      return;
    }
    setPending(p);
  }, [strategyCtx]);

  const confirmReplace = useCallback(() => {
    if (!pending) return;
    strategyCtx.setStrategy(pending.blocks, pending.name, pending.source, pending.meta);
    pending.onComplete?.();
    setPending(null);
  }, [pending, strategyCtx]);

  const cancelReplace = useCallback(() => setPending(null), []);

  return { pending, requestReplace, confirmReplace, cancelReplace };
}

export const ReplaceStrategyConfirm: React.FC<{
  pending: PendingReplace | null;
  onConfirm: () => void;
  onCancel: () => void;
  // When provided, shows a "Save current first" button (the caller cancels + opens its Save dialog).
  onSaveFirst?: () => void;
}> = ({ pending, onConfirm, onCancel, onSaveFirst }) => {
  const strategyCtx = useStrategy();
  useEscapeKey(onCancel, !!pending);
  if (!pending) return null;
  const isNew = pending.kind === 'new';

  const currentName  = strategyCtx.strategyName ?? '(unnamed)';
  const blockCount   = strategyCtx.blocks.length;
  const loadedAt     = strategyCtx.loadedAt;
  const ageMins      = loadedAt ? Math.floor((Date.now() - loadedAt) / 60_000) : null;
  const ageLabel     = ageMins == null ? ''
                     : ageMins < 1   ? 'just now'
                     : ageMins < 60  ? `${ageMins}m ago`
                     :                 `${Math.floor(ageMins / 60)}h ago`;
  const nextDisplay  = pending.label ?? pending.name ?? 'the new variant';

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-labelledby="replace-strategy-confirm-title"
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <h2 id="replace-strategy-confirm-title" className="font-semibold text-gray-900">
              {isNew ? 'Start a new strategy?' : 'Replace your current strategy?'}
            </h2>
          </div>
          <button
            onClick={onCancel}
            className="p-1 hover:bg-gray-100 rounded"
            aria-label="Cancel"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 text-sm text-gray-700 space-y-2">
          <p>
            Your current strategy <strong>&ldquo;{currentName}&rdquo;</strong>
            {' '}({blockCount} block{blockCount === 1 ? '' : 's'}{ageLabel ? `, last modified ${ageLabel}` : ''})
            {' '}has <strong>unsaved edits</strong>.
          </p>
          <p>
            {isNew
              ? <>Starting a new strategy will <strong>clear the canvas</strong> and discard these edits.</>
              : <>Loading <strong>&ldquo;{nextDisplay}&rdquo;</strong> will overwrite it.</>}
          </p>
          <p className="text-xs text-gray-500 italic pt-1">
            {onSaveFirst
              ? <>Want to keep it? Use <strong>Save current first</strong> below.</>
              : <>Want to keep your current work? Cancel, then save it from the Backtest tab via <strong>Save Run</strong>.</>}
          </p>
        </div>
        <div className="flex items-center justify-end gap-2 p-3 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onCancel}
            autoFocus
            className="px-4 py-1.5 text-sm text-gray-700 hover:bg-gray-200 rounded font-medium"
          >
            Cancel
          </button>
          {onSaveFirst && (
            <button
              onClick={onSaveFirst}
              className="px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded font-medium"
            >
              Save current first
            </button>
          )}
          <button
            onClick={onConfirm}
            className="px-4 py-1.5 text-sm bg-amber-600 hover:bg-amber-700 text-white rounded font-medium"
          >
            {pending.confirmLabel ?? 'Replace'}
          </button>
        </div>
      </div>
    </div>
  );
};
